import { Alert, Button, Card, Divider, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    Grid as ZhGrid,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { BANK_CERT, INVOICE, INVOICE_HEADER } from "@/meta/customer/customer";
import { CHANNEL, COMMUNICATE_MODULE, INTENTION } from "@zhihaoscm/meta";
import type { CustomerInfo } from "@zhihaoscm/entity";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";
import {
    CustomerCommunicationPage,
    type CustomerCommunicationQuery,
    type CustomerCommunicationRecord,
    loadCustomerCommunicationRecords
} from "./CustomerCommunicationPage";
import {
    CustomerLogisticsPage,
    type CustomerLogisticsQuery,
    type CustomerLogisticsRecord,
    loadCustomerLogistics
} from "./CustomerLogisticsPage";
import {
    CustomerProfilePage,
    type CustomerProfileBasic,
    type CustomerProfileOverview,
    type CustomerProfileStats,
    loadCustomerProfileBasic,
    loadCustomerProfileOverview,
    loadCustomerProfileStats
} from "./CustomerProfilePage";
import { CustomerSubAccountPage } from "./CustomerSubAccountPage";

interface CustomerDetailCustomer {
    id: number;
    code?: string;
    mobile?: string;
    email?: string;
    nickName?: string;
    realName?: string;
    idNo?: string;
    createdTime?: string;
    updatedTime?: string;
    institutionName?: string;
    unifiedSocialCreditCode?: string;
    legalRepresentative?: string;
    memberLevel?: number | string;
    memberState?: number | string;
    memberActiveTime?: string;
    memberExpiryDate?: string;
    state?: number | string;
}

export interface CustomerInfoDetailRecord {
    customer: CustomerDetailCustomer;
    customerInfo?: CustomerInfo;
    institutionApply?: {
        institutionName?: string;
        unifiedSocialCreditCode?: string;
        legalRepresentative?: string;
    };
    isCancelInstitutionApply?: boolean;
    addresses?: Array<{
        id?: number;
        contacts?: string;
        institutionName?: string;
        cellphone?: string;
        areaCode?: string;
        landline?: string;
        zipCode?: string;
        detailAddress?: string;
        isDefault?: number;
    }>;
    banks?: Array<{
        bank?: { id?: number; name?: string; account?: string; bank?: string; type?: number };
        bankDict?: { bankName?: string };
    }>;
    invoiceHeaders?: Array<{
        id?: number;
        name?: string;
        headerType?: number;
        invoiceType?: number;
        taxpayerNo?: string;
        bank?: string;
        account?: string;
        mobile?: string | number;
        address?: string;
        isDefault?: number;
    }>;
}

export interface CustomerInfoDetailPageProps {
    userId?: number;
    loadDetail?: (id: number) => Promise<CustomerInfoDetailRecord>;
    cancelApply?: (id: number) => Promise<unknown>;
    loadLogistics?: (query: CustomerLogisticsQuery) => Promise<{ list?: CustomerLogisticsRecord[]; total?: number }>;
    loadCommunication?: (
        query: CustomerCommunicationQuery
    ) => Promise<{ list?: CustomerCommunicationRecord[]; total?: number }>;
    loadProfileBasic?: (params: { customerId: number }) => Promise<CustomerProfileBasic>;
    loadProfileOverview?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileOverview>;
    loadProfileStats?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileStats>;
    loadRechargeRecords?: (query: CustomerRechargeQuery) => Promise<{
        list?: CustomerRechargeRow[];
        total?: number;
    }>;
    loadRechargeAmount?: (customerId: number) => Promise<number>;
    canWriteCommunication?: boolean;
}

export interface CustomerRechargeQuery {
    page: number;
    size: number;
    customerId: number;
}

export interface CustomerRechargeRow {
    id: number | string;
    memberLevel?: number | string;
    memberDay?: number;
    openMode?: number | string;
    activationCode?: string;
    amount?: number;
    activationDate?: string;
    expireDate?: string;
    createdTime?: string;
}

const memberLevelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const memberStateLabels: Record<number, string> = { 1: "正常", 2: "已过期" };
const userStateLabels: Record<number, string> = { 1: "正常", 2: "禁用", 3: "已注销" };

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 复用 Vue `/admin/customer/find/:id` 用户详情接口。 */
export async function loadCustomerDetail(id: number): Promise<CustomerInfoDetailRecord> {
    const response = await request.service.get(`/admin/customer/find/${id}`);
    return response.data;
}

/** 保留 Vue 解除机构认证接口。 */
export async function cancelCustomerInstitutionApply(id: number) {
    const response = await request.service.put(`/admin/customer/cancelApply/${id}`);
    return response.data;
}

/** 复用 Vue 会员信息组件的充值记录分页接口，详情页只追加当前用户过滤条件。 */
export async function loadCustomerRechargeRecords(query: CustomerRechargeQuery) {
    const response = await request.service.get("/admin/member-open-record/paging", { params: query });
    return response.data as { list?: CustomerRechargeRow[]; total?: number };
}

/** 复用 Vue membershipInfo.vue 的总计实收金额接口。 */
export async function loadCustomerRechargeAmount(customerId: number) {
    const response = await request.service.get(`/admin/member-open-record/amount?customerId=${customerId}`);
    return Number(response.data || 0);
}

const membershipDays = (activeTime?: string, expiryTime?: string, state?: number | string) => {
    if (!activeTime || !expiryTime || Number(state) === 2) return "--";
    const days = Math.max(0, Math.round((new Date(expiryTime).getTime() - new Date(activeTime).getTime()) / 86400000));
    return `${days} 天`;
};

const rechargeMemberLevelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const rechargeOpenModeLabels: Record<number, string> = {
    1: "微信支付",
    2: "支付宝",
    3: "激活码",
    4: "APPLE PAY"
};

/** 充值记录金额与 Vue 页面保持两位小数展示。 */
const formatRechargeAmount = (value?: number) => (value == null ? DEFAULT_VALUE : Number(value).toFixed(2));

/** 会员详情中的充值记录面板，负责分页数据的异步状态与核心字段展示。 */
function CustomerRechargeRecords({
    customerId,
    loadRecords,
    loadAmount
}: {
    customerId: number;
    loadRecords: (query: CustomerRechargeQuery) => Promise<{
        list?: CustomerRechargeRow[];
        total?: number;
    }>;
    loadAmount: (customerId: number) => Promise<number>;
}) {
    const [records, setRecords] = useState<CustomerRechargeRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [amount, setAmount] = useState<number>();

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        setErrorMessage("");

        // 与 Vue membershipInfo.vue 一致，查询参数必须带当前详情用户 customerId。
        void loadRecords({ page: 1, size: 20, customerId })
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("会员充值记录加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [customerId, loadRecords]);

    useEffect(() => {
        let disposed = false;
        void loadAmount(customerId)
            .then(value => {
                if (!disposed) setAmount(value);
            })
            .catch(() => {
                if (!disposed) setAmount(0);
            });
        return () => {
            disposed = true;
        };
    }, [customerId, loadAmount]);

    // 用 zhihao-ui-react 列契约承接旧 Vue 会员充值记录的字段展示。
    const columns: TableColumnProps<CustomerRechargeRow>[] = [
        { key: "id", label: "编号", prop: "id" },
        {
            key: "memberLevel",
            label: "会员等级",
            prop: "memberLevel",
            render: ({ row }) => rechargeMemberLevelLabels[Number(row.memberLevel)] || DEFAULT_VALUE
        },
        { key: "memberDay", label: "时长", prop: "memberDay", render: ({ row }) => `${row.memberDay || 0}天` },
        {
            key: "openMode",
            label: "支付/兑换方式",
            prop: "openMode",
            render: ({ row }) => rechargeOpenModeLabels[Number(row.openMode)] || DEFAULT_VALUE
        },
        { key: "activationCode", label: "激活码", prop: "activationCode" },
        {
            key: "amount",
            label: "实收金额（元）",
            prop: "amount",
            render: ({ row }) => formatRechargeAmount(row.amount)
        },
        {
            key: "activationDate",
            label: "激活日期",
            prop: "activationDate",
            render: ({ row }) => formatDate(row.activationDate)
        },
        {
            key: "expireDate",
            label: "到期日期",
            prop: "expireDate",
            render: ({ row }) => formatDate(row.expireDate)
        }
    ];

    return (
        <section aria-label="customer-recharge-records">
            <ZhTable<CustomerRechargeRow>
                data={records}
                columns={columns}
                emptyText={loading ? "加载中..." : "暂无充值记录"}
                pagination={false}
                rowKey="id"
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <Typography.Title level={5}>充值记录</Typography.Title>

                        <p>总计实收金额：{amount == null ? DEFAULT_VALUE : amount.toFixed(2)} 元</p>

                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination current={1} pageSize={20} total={total} hideOnSinglePage />
                    </div>
                }
            />
        </section>
    );
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** 旧版 InfoCard 的 React 迁移实现，使用 zhihao-ui-react Grid/BaseItem 保持详情字段布局。 */
function CustomerAssociatedInfo({ detail }: { detail: CustomerInfoDetailRecord }) {
    const addresses = detail.addresses || [];
    const banks = detail.banks || [];
    const invoiceHeaders = detail.invoiceHeaders || [];
    // BaseItem 的值使用 fragment 承接，避免组件同时渲染可见值和辅助值造成字段重复。
    const baseValue = (value: string) => <>{value}</>;
    return (
        <section aria-label="customer-associated-info">
            <Typography.Title level={5}>地址簿</Typography.Title>
            {addresses.length ? (
                addresses.map(address => (
                    <Card
                        key={address.id}
                        className="mb-4"
                        title={`${display(address.contacts)}${address.isDefault ? "（默认）" : ""}`}
                    >
                        <ZhGrid cols={2} gap={[20, 12]}>
                            <ZhBaseItem label="机构名称" value={baseValue(display(address.institutionName))} />
                            <ZhBaseItem label="手机号码" value={baseValue(display(address.cellphone))} />
                            <ZhBaseItem
                                label="固定电话"
                                value={baseValue(
                                    address.landline
                                        ? `${address.areaCode ? `${address.areaCode}-` : ""}${address.landline}`
                                        : DEFAULT_VALUE
                                )}
                            />
                            <ZhBaseItem label="邮政编码" value={baseValue(display(address.zipCode))} />
                            <ZhBaseItem label="地址" value={baseValue(display(address.detailAddress))} span={2} />
                        </ZhGrid>
                    </Card>
                ))
            ) : (
                <p>暂无数据</p>
            )}
            <Typography.Title level={5}>银行账户</Typography.Title>
            {banks.length ? (
                banks.map(bank => (
                    <Card
                        key={bank.bank?.id}
                        className="mb-4"
                        title={display(bank.bankDict?.bankName || bank.bank?.bank)}
                    >
                        <ZhGrid cols={2} gap={[20, 12]}>
                            <ZhBaseItem
                                label="账户类型"
                                value={baseValue(BANK_CERT.getIntroByCode(bank.bank?.type) || DEFAULT_VALUE)}
                            />
                            <ZhBaseItem label="开户名称" value={baseValue(display(bank.bank?.name))} />
                            <ZhBaseItem label="银行账户" value={baseValue(display(bank.bank?.account))} />
                            <ZhBaseItem
                                label="银行"
                                value={baseValue(display(bank.bankDict?.bankName || bank.bank?.bank))}
                            />
                        </ZhGrid>
                    </Card>
                ))
            ) : (
                <p>暂无数据</p>
            )}
            <Typography.Title level={5}>发票抬头</Typography.Title>
            {invoiceHeaders.length ? (
                invoiceHeaders.map(invoice => (
                    <Card
                        key={invoice.id}
                        className="mb-4"
                        title={`${display(invoice.name)}${invoice.isDefault ? "（默认）" : ""}`}
                    >
                        <ZhGrid cols={2} gap={[20, 12]}>
                            <ZhBaseItem
                                label="抬头类型"
                                value={baseValue(INVOICE_HEADER.getIntroByCode(invoice.headerType) || DEFAULT_VALUE)}
                            />
                            <ZhBaseItem
                                label="发票类型"
                                value={baseValue(INVOICE.getIntroByCode(invoice.invoiceType) || DEFAULT_VALUE)}
                            />
                            <ZhBaseItem label="纳税人识别号" value={baseValue(display(invoice.taxpayerNo))} />
                            <ZhBaseItem label="开户行" value={baseValue(display(invoice.bank))} />
                            <ZhBaseItem label="账号" value={baseValue(display(invoice.account))} />
                            <ZhBaseItem label="电话" value={baseValue(display(invoice.mobile))} />
                            <ZhBaseItem label="地址" value={baseValue(display(invoice.address))} span={2} />
                        </ZhGrid>
                    </Card>
                ))
            ) : (
                <p>暂无数据</p>
            )}
        </section>
    );
}

/** 迁移旧版 interactionRecord/userInfo/detail.vue，保留 zhihao-ui-react 网格字段契约。 */
function CustomerCommunicationUserInfo({ info }: { info?: CustomerInfo }) {
    const intention = info?.intention ? INTENTION.getIntroByCode(info.intention)?.slice(0, 1) : DEFAULT_VALUE;
    const yesNo = (value?: number) => (value == null ? DEFAULT_VALUE : value ? "是" : "否");
    const concernModule = info?.concernModule?.length
        ? info.concernModule.map(item => COMMUNICATE_MODULE.getIntroByCode(item)).join("、")
        : DEFAULT_VALUE;
    const userLabel = info?.userLabel?.length ? info.userLabel.join("、") : DEFAULT_VALUE;
    // 详情接口只保证返回区域编码；沿用旧页面的可辨识区域展示，避免引入 Vue-only areaLib 到 React 类型检查。
    const region =
        info?.provinceCode || info?.cityCode
            ? [info.provinceCode, info.cityCode].filter(Boolean).join("/")
            : DEFAULT_VALUE;
    return (
        <section aria-label="customer-communication-user-info" className="mb-4">
            <Typography.Title level={5}>用户信息</Typography.Title>
            <ZhGrid cols={4} gap={[20, 20]}>
                <ZhBaseItem label="姓名" value={display(info?.name)} />
                <ZhBaseItem label="区域" value={region} />
                <ZhBaseItem label="了解渠道" value={CHANNEL.getIntroByCode(info?.channel) || DEFAULT_VALUE} />
                <ZhBaseItem label="意向度" value={intention} />
                <ZhBaseItem label="是否为砂石行业" value={yesNo(info?.sandFlag)} />
                <ZhBaseItem label="行业" value={display(info?.industry)} />
                <ZhBaseItem label="是否需要入驻平台" value={yesNo(info?.settlePlatformFlag)} />
                <ZhBaseItem label="关注模块" value={concernModule} />
                <ZhBaseItem label="用户标签" value={userLabel} span={4} />
            </ZhGrid>
        </section>
    );
}

export function CustomerInfoDetailPage({
    userId,
    loadDetail = loadCustomerDetail,
    cancelApply = cancelCustomerInstitutionApply,
    loadLogistics = loadCustomerLogistics,
    loadCommunication = loadCustomerCommunicationRecords,
    loadProfileBasic = loadCustomerProfileBasic,
    loadProfileOverview = loadCustomerProfileOverview,
    loadProfileStats = loadCustomerProfileStats,
    loadRechargeRecords = loadCustomerRechargeRecords,
    loadRechargeAmount = loadCustomerRechargeAmount,
    canWriteCommunication = true
}: CustomerInfoDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const banButton = useUserStore(user => user.banButton);
    // Vue 详情页从 route.query.userId 取值；保留 prop 覆盖能力供嵌入式调用和测试使用。
    const routeUserId = new URLSearchParams(location.search).get("userId");
    const resolvedUserId =
        userId ?? (routeUserId && Number.isFinite(Number(routeUserId)) ? Number(routeUserId) : undefined);
    const [detail, setDetail] = useState<CustomerInfoDetailRecord>();
    const [activeTab, setActiveTab] = useState<
        "base" | "member" | "subAccount" | "logistics" | "communication" | "profile"
    >("base");
    const [errorMessage, setErrorMessage] = useState("");
    const [operationMessage, setOperationMessage] = useState("");

    const fetchDetail = () => {
        if (resolvedUserId == null) return Promise.resolve();
        return loadDetail(resolvedUserId)
            .then(result => {
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => setErrorMessage("用户详情加载失败"));
    };

    useEffect(() => {
        let disposed = false;
        if (resolvedUserId == null) return undefined;
        void loadDetail(resolvedUserId)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setErrorMessage("");
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedUserId]);

    const customer = detail?.customer;
    const institutionName = customer?.institutionName || detail?.institutionApply?.institutionName;
    const creditCode = customer?.unifiedSocialCreditCode || detail?.institutionApply?.unifiedSocialCreditCode;
    const legalRepresentative = customer?.legalRepresentative || detail?.institutionApply?.legalRepresentative;

    const handleCancelApply = async () => {
        if (customer?.id == null) return;
        try {
            await cancelApply(customer.id);
            setOperationMessage("机构认证已解除");
            await fetchDetail();
        } catch {
            setOperationMessage("解除机构认证失败");
        }
    };

    return (
        <section aria-label="customer-info-detail-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Space className="mb-3" wrap>
                    <Typography.Title className="mb-0 mt-0" level={4}>
                        用户详情
                    </Typography.Title>
                    <span>更新时间：{formatDate(customer?.updatedTime)}</span>
                    <span>用户状态：{userStateLabels[Number(customer?.state)] || DEFAULT_VALUE}</span>
                </Space>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {operationMessage ? <Alert className="mb-4" message={operationMessage} type="success" /> : null}
                <Space>
                    <Button
                        aria-label="基本信息"
                        type={activeTab === "base" ? "primary" : "default"}
                        onClick={() => setActiveTab("base")}
                    >
                        基本信息
                    </Button>
                    <Button
                        aria-label="会员信息"
                        type={activeTab === "member" ? "primary" : "default"}
                        onClick={() => setActiveTab("member")}
                    >
                        会员信息
                    </Button>
                    <Button
                        aria-label="子账号信息"
                        type={activeTab === "subAccount" ? "primary" : "default"}
                        onClick={() => setActiveTab("subAccount")}
                    >
                        子账号信息
                    </Button>
                    <Button
                        aria-label="物流信息"
                        type={activeTab === "logistics" ? "primary" : "default"}
                        onClick={() => setActiveTab("logistics")}
                    >
                        物流信息
                    </Button>
                    <Button
                        aria-label="用户沟通记录"
                        type={activeTab === "communication" ? "primary" : "default"}
                        onClick={() => setActiveTab("communication")}
                    >
                        用户沟通记录
                    </Button>
                    <Button
                        aria-label="用户画像"
                        type={activeTab === "profile" ? "primary" : "default"}
                        onClick={() => setActiveTab("profile")}
                    >
                        用户画像
                    </Button>
                </Space>
                <Divider />
                {activeTab === "profile" && customer?.id != null ? (
                    <CustomerProfilePage
                        customerId={customer.id}
                        loadBasic={loadProfileBasic}
                        loadOverview={loadProfileOverview}
                        loadStats={loadProfileStats}
                    />
                ) : activeTab === "communication" && customer?.id != null ? (
                    <>
                        <CustomerCommunicationUserInfo info={detail?.customerInfo} />
                        <CustomerCommunicationPage
                            customerId={customer.id}
                            canRead
                            canWrite={canWriteCommunication}
                            loadRecords={loadCommunication}
                        />
                    </>
                ) : activeTab === "logistics" && customer?.id != null ? (
                    <CustomerLogisticsPage customerId={customer.id} loadLogistics={loadLogistics} />
                ) : activeTab === "subAccount" && customer?.id != null ? (
                    <CustomerSubAccountPage mainAccountId={customer.id} />
                ) : activeTab === "base" ? (
                    <>
                        <Typography.Title level={5}>账号信息</Typography.Title>
                        <p>用户ID：{customer?.code || DEFAULT_VALUE}</p>
                        <p>注册手机号：{customer?.mobile || DEFAULT_VALUE}</p>
                        <p>注册时间：{formatDate(customer?.createdTime)}</p>
                        <p>绑定微信：{customer?.nickName || DEFAULT_VALUE}</p>
                        <p>绑定邮箱：{customer?.email || DEFAULT_VALUE}</p>
                        <Typography.Title level={5}>个人认证信息</Typography.Title>
                        <p>个人实名：{customer?.realName || DEFAULT_VALUE}</p>
                        <p>身份证号：{customer?.idNo || "未认证"}</p>
                        <Typography.Title level={5}>组织机构信息</Typography.Title>
                        <p>组织机构名称：{institutionName || "未认证"}</p>
                        <p>统一社会信用代码：{creditCode || "未认证"}</p>
                        <p>法人代表：{legalRepresentative || "未认证"}</p>
                        <Typography.Title level={5}>关联信息</Typography.Title>
                        <p>地址簿：{detail?.addresses?.length || 0} 条</p>
                        <p>银行账户：{detail?.banks?.length || 0} 条</p>
                        <p>发票抬头：{detail?.invoiceHeaders?.length || 0} 条</p>
                        {detail ? <CustomerAssociatedInfo detail={detail} /> : null}
                        {detail?.isCancelInstitutionApply ? (
                            <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                                <Button disabled={banButton} type="primary" onClick={() => void handleCancelApply()}>
                                    解除机构认证
                                </Button>
                            </Permission>
                        ) : null}
                    </>
                ) : (
                    <>
                        <Typography.Title level={5}>会员信息</Typography.Title>
                        <p>会员等级：{memberLevelLabels[Number(customer?.memberLevel)] || DEFAULT_VALUE}</p>
                        <p>会员状态：{memberStateLabels[Number(customer?.memberState)] || DEFAULT_VALUE}</p>
                        <p>
                            会员时长：
                            {membershipDays(
                                customer?.memberActiveTime,
                                customer?.memberExpiryDate,
                                customer?.memberState
                            )}
                        </p>
                        <p>激活日期：{formatDate(customer?.memberActiveTime)}</p>
                        <p>到期时间：{formatDate(customer?.memberExpiryDate)}</p>
                        {customer?.id != null ? (
                            <CustomerRechargeRecords
                                customerId={customer.id}
                                loadRecords={loadRechargeRecords}
                                loadAmount={loadRechargeAmount}
                            />
                        ) : (
                            <p>充值记录：{DEFAULT_VALUE}</p>
                        )}
                    </>
                )}
                <Space className="mt-4">
                    {/* 对齐旧版 infoDetail 的固定返回操作，回到进入详情前的历史页面。 */}
                    <Button aria-label="返回" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
        </section>
    );
}
